/**
 * Resolving a `?book=` deep link against the catalog the services page already
 * has in hand.
 *
 * The variation pages hand out these slugs and the services page loads every
 * service with every variation, so entering the booking flow at a specific
 * variation needs no extra lookup — just a find().
 */

import { BOOKING_PHASES, type Phase } from './booking-phases';

import { type Service, type ServiceVariation } from './services';

export const BOOK_PARAM = 'book';

export const readBookSlug = (search: string) => new URLSearchParams(search).get(BOOK_PARAM);

export const findVariationBySlug = (
  services: Service[],
  slug: string,
): { service: Service; variation: ServiceVariation } | null => {
  const service = services.find((candidate) => candidate.variations.some((v) => v.sku === slug));
  const variation = service?.variations.find((v) => v.sku === slug);

  // An ad can outlive the variation it points at, so a stale slug resolves to
  // null and the caller falls back to the normal listing.
  return service && variation ? { service, variation } : null;
};

/**
 * Going back from the payment step renders the category's service list, so a
 * deep link has to resolve a category up front even though the step it lands
 * on never shows one.
 */
export const findCategoryForService = (categories: any[], service: Service) =>
  categories.find((category: any) => category.slug === service.categories[0]) ?? null;

type FlowUrlInput = {
  pathname: string;
  search: string;
  phase: Phase;
  variationSlug?: string | null;
  categorySlug?: string | null;
};

/**
 * The URL that describes the step the visitor is on: `?book=` while they are on
 * the booking steps, so a refresh resumes there, and `#category` once they step
 * back out.
 *
 * Every other query parameter is carried over. Ad platforms append gclid and
 * utm_*, and this runs on mount — dropping them here loses the campaign
 * attribution before analytics has necessarily read it.
 */
export const buildFlowUrl = (input: FlowUrlInput) => {
  const { pathname, search, phase, variationSlug, categorySlug } = input;

  const params = new URLSearchParams(search);
  const isBooking = BOOKING_PHASES.includes(phase);

  if (isBooking && variationSlug) params.set(BOOK_PARAM, variationSlug);
  else params.delete(BOOK_PARAM);

  const query = params.toString();
  const hash = !isBooking && categorySlug ? `#${categorySlug}` : '';

  return `${pathname}${query ? `?${query}` : ''}${hash}`;
};
